import { Container, Graphics } from "pixi.js";
import {
  type ProjectileKind,
  type ProjectileSnapshot,
  SERVER_TICK_MS,
} from "@vinyl/shared";
import { tileToScreen } from "./iso.js";

const IMPACT_FLASH_MS = 180;
// Visual height above ground so the orb reads as flying. Pure cosmetic.
const PROJECTILE_FLIGHT_HEIGHT = 14;

export interface ProjectileSpawnPayload {
  projectileId: string;
  kind: ProjectileKind;
  attackerId: string;
  attackerKind: "player" | "mob";
  originX: number;
  originY: number;
}

export interface ProjectileExpirePayload {
  projectileId: string;
  x: number;
  y: number;
  reason: "land" | "wall" | "lifetime" | "lost";
}

interface ProjectileView {
  container: Container;
  projectileId: string;
  kind: ProjectileKind;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  lerpStart: number;
  lerpDuration: number;
  flashUntil: number; // 0 until expired
  // Set by expire(): the orb first finishes its visible travel to the impact
  // point (short lerp), THEN the flash plays. Without this the ball vanished
  // wherever its (lagging) interpolation happened to be — up to ~2 tiles
  // short of the target — and the flash popped far ahead of it, reading as
  // "the spell stopped before reaching the target".
  pendingFlashReason: "land" | "wall" | "lifetime" | "lost" | null;
}

// Lerp duration when receiving a fresh snapshot position. Projectiles are
// FAST (14 tiles/s): the mob-style smoothing (tick + render delay = 150 ms)
// made the rendered orb trail ~2 tiles behind its server position (the lerp
// restarts every 50 ms tick and only ever completes a third of each segment).
// One server tick of smoothing keeps the motion fluid with ~0.7 tile of lag.
const SNAPSHOT_LERP_MS = SERVER_TICK_MS;
// Duration of the final travel-to-impact segment played on expire.
const IMPACT_TRAVEL_MS = 90;

export class ProjectileLayer {
  readonly container = new Container();
  private readonly views = new Map<string, ProjectileView>();

  constructor() {
    this.container.label = "projectiles";
  }

  spawn(payload: ProjectileSpawnPayload, perfNow: number): void {
    if (this.views.has(payload.projectileId)) return;
    const pos = tileToScreen(payload.originX, payload.originY);
    const sx = pos.sx;
    const sy = pos.sy - PROJECTILE_FLIGHT_HEIGHT;
    const view: ProjectileView = {
      container: createProjectileSprite(payload.kind),
      projectileId: payload.projectileId,
      kind: payload.kind,
      fromX: sx,
      fromY: sy,
      toX: sx,
      toY: sy,
      lerpStart: perfNow,
      lerpDuration: 0,
      flashUntil: 0,
      pendingFlashReason: null,
    };
    view.container.position.set(sx, sy);
    this.container.addChild(view.container);
    this.views.set(payload.projectileId, view);
  }

  ingestSnapshot(snapshots: ProjectileSnapshot[], perfNow: number): void {
    for (const s of snapshots) {
      const view = this.views.get(s.id);
      if (!view) {
        // Snapshot arrived before the projectileSpawned message (or we missed
        // the spawn). Drop a sprite at the reported tile so it still renders.
        this.spawn(
          {
            projectileId: s.id,
            kind: s.kind,
            attackerId: "",
            attackerKind: "mob",
            originX: s.x,
            originY: s.y,
          },
          perfNow,
        );
        continue;
      }
      if (view.flashUntil > 0 || view.pendingFlashReason) continue; // already exploded / landing
      const target = tileToScreen(s.x, s.y);
      const tx = target.sx;
      const ty = target.sy - PROJECTILE_FLIGHT_HEIGHT;
      const cur = lerpPos(view, perfNow);
      view.fromX = cur.x;
      view.fromY = cur.y;
      view.toX = tx;
      view.toY = ty;
      view.lerpStart = perfNow;
      view.lerpDuration = SNAPSHOT_LERP_MS;
    }
    // We deliberately do NOT remove projectiles missing from a snapshot —
    // server sends an explicit projectileExpired with a final tile so the
    // impact flash can play at the right place. Clearing on snapshot-miss
    // would race that event.
  }

  expire(payload: ProjectileExpirePayload, perfNow: number): void {
    let view = this.views.get(payload.projectileId);
    if (!view) {
      // Spawn-missed AND snapshot-missed: still play the flash so the player
      // gets feedback at the impact tile.
      this.spawn(
        {
          projectileId: payload.projectileId,
          kind: "arcane_orb",
          attackerId: "",
          attackerKind: "mob",
          originX: payload.x,
          originY: payload.y,
        },
        perfNow,
      );
      view = this.views.get(payload.projectileId)!;
    }
    if (view.flashUntil > 0 || view.pendingFlashReason) return;
    // Finish the visible travel: lerp from wherever the (lagging) orb is
    // rendered right now to the impact point, then flash (see update()).
    const target = tileToScreen(payload.x, payload.y);
    const cur = lerpPos(view, perfNow);
    view.fromX = cur.x;
    view.fromY = cur.y;
    view.toX = target.sx;
    view.toY = target.sy - PROJECTILE_FLIGHT_HEIGHT;
    view.lerpStart = perfNow;
    view.lerpDuration = IMPACT_TRAVEL_MS;
    view.pendingFlashReason = payload.reason;
  }

  update(perfNow: number): void {
    if (this.views.size === 0) return;
    for (const [id, view] of this.views) {
      if (view.flashUntil > 0) {
        if (perfNow >= view.flashUntil) {
          this.removeView(id);
        } else {
          const remaining = view.flashUntil - perfNow;
          view.container.alpha = Math.max(0, remaining / IMPACT_FLASH_MS);
        }
        continue;
      }
      const cur = lerpPos(view, perfNow);
      view.container.position.set(cur.x, cur.y);
      // Travel-to-impact finished → swap the ball for the flash, in place.
      if (
        view.pendingFlashReason &&
        perfNow >= view.lerpStart + view.lerpDuration
      ) {
        view.container.removeChildren();
        view.container.addChild(
          createImpactFlash(view.kind, view.pendingFlashReason),
        );
        view.flashUntil = perfNow + IMPACT_FLASH_MS;
        view.pendingFlashReason = null;
      }
    }
  }

  clear(): void {
    for (const id of [...this.views.keys()]) this.removeView(id);
  }

  private removeView(id: string): void {
    const view = this.views.get(id);
    if (!view) return;
    this.container.removeChild(view.container);
    view.container.destroy({ children: true });
    this.views.delete(id);
  }
}

function lerpPos(view: ProjectileView, now: number): { x: number; y: number } {
  if (view.lerpDuration <= 0) return { x: view.toX, y: view.toY };
  const elapsed = now - view.lerpStart;
  if (elapsed >= view.lerpDuration) return { x: view.toX, y: view.toY };
  const t = Math.max(0, elapsed / view.lerpDuration);
  return {
    x: view.fromX + (view.toX - view.fromX) * t,
    y: view.fromY + (view.toY - view.fromY) * t,
  };
}

function createProjectileSprite(kind: ProjectileKind): Container {
  const c = new Container();
  c.label = `projectile:${kind}`;
  switch (kind) {
    case "arcane_orb": {
      const glow = new Graphics()
        .circle(0, 0, 9)
        .fill({ color: 0xa06bff, alpha: 0.3 });
      const core = new Graphics()
        .circle(0, 0, 5)
        .fill({ color: 0xc8a2ff })
        .stroke({ color: 0x4a1f8a, alpha: 0.9, width: 1 });
      c.addChild(glow, core);
      break;
    }
  }
  return c;
}

function createImpactFlash(
  kind: ProjectileKind,
  reason: "land" | "wall" | "lifetime" | "lost",
): Container {
  const c = new Container();
  c.label = `impact:${kind}:${reason}`;
  // Dim grey for non-hit outcomes (the spell fizzled), bright purple for land.
  const isHit = reason === "land";
  const ringColor = isHit ? 0xa06bff : 0x6b6b78;
  const coreColor = isHit ? 0xe6d0ff : 0x9a9aa6;
  const ring = new Graphics()
    .circle(0, 0, 14)
    .stroke({ color: ringColor, alpha: 0.85, width: 2 });
  const core = new Graphics()
    .circle(0, 0, 6)
    .fill({ color: coreColor, alpha: 0.7 });
  c.addChild(ring, core);
  return c;
}
